import React from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FF9A5A] to-[#FF5F52] text-white antialiased font-sans scroll-smooth">
      {/* ================= HEADER ================= */}
      <header className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between">
        <div className="font-bold text-lg">BiteMark</div>
      </header>

      {/* ================= MAIN ================= */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-bold mb-6">Privacy Policy</h1>

        <p className="text-white/80 mb-4">
          At BiteMark, your privacy and safety are our top priority. This policy explains what information we collect, how it is used, and the control you have over your data.
        </p>

        {/* ================= SECTION 1 ================= */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">1. Information We Collect</h2>
        <p className="text-white/80 mb-4">
          BiteMark only collects the information you provide when creating an account:
        </p>
        <ul className="list-disc list-inside text-white/80 mb-4">
          <li>Email address</li>
          <li>Username or display name</li>
        </ul>
        <p className="text-white/80 mb-4">
          We do <strong>not</strong> collect background device data, contacts, photos, or any information unrelated to the core functionality of the app.
        </p>

        {/* ================= SECTION 2 ================= */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">2. How We Use Your Data</h2>
        <p className="text-white/80 mb-4">
          Your account information is used solely to provide BiteMark features such as:
        </p>
        <ul className="list-disc list-inside text-white/80 mb-4">
          <li>Saving and viewing restaurant locations</li>
          <li>Managing your personal food map</li>
          <li>Syncing your account across devices</li>
        </ul>
        <p className="text-white/80 mb-4">
          BiteMark does <strong>not</strong> sell, rent, or share your data with third parties, and we do not use your data for advertising or tracking.
        </p>

        {/* ================= SECTION 3 ================= */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">3. Device Permissions</h2>
        <p className="text-white/80 mb-4">
          Certain BiteMark features may request access to your device location in order to place map pins or save restaurant locations. Location access is:
        </p>
        <ul className="list-disc list-inside text-white/80 mb-4">
          <li>Only requested when needed</li>
          <li>Used strictly for app functionality</li>
          <li>Never used for tracking or advertising</li>
        </ul>
        <p className="text-white/80 mb-4">
          You can manage or revoke location permissions at any time through your device settings.
        </p>

        {/* ================= SECTION 4 ================= */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">4. Account & Data Deletion</h2>
        <p className="text-white/80 mb-4">
          You are always in full control of your data.
        </p>
        <p className="text-white/80 mb-4">
          Users can delete their BiteMark account and all associated data at any time, without restrictions, directly from within the app’s settings.
        </p>
        <p className="text-white/80 mb-4">
          Once an account deletion request is submitted, all associated data is permanently removed from our systems.
        </p>
        <p className="text-white/80 mb-4">
          If you are unable to access the app, you may also request data deletion by emailing{" "}
          <a
            href="mailto:thebitemarkapp@gmail.com"
            className="underline hover:text-[#FFD369]"
          >
            thebitemarkapp@gmail.com
          </a>{" "}
          with the subject line <strong>“Data Deletion Request”</strong>.
        </p>

        {/* ================= SECTION 5 ================= */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">5. Security</h2>
        <p className="text-white/80 mb-4">
          We use industry-standard security practices to protect your information. We recommend securing your device with a passcode, Face ID, or Touch ID for additional protection.
        </p>

        {/* ================= SECTION 6 ================= */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">6. Children & Families</h2>
        <p className="text-white/80 mb-4">
          BiteMark is designed for users of all ages. We do not knowingly collect sensitive personal data from children under the age of 13.
        </p>

        {/* ================= SECTION 7 ================= */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">7. No Third-Party Tracking</h2>
        <p className="text-white/80 mb-4">
          BiteMark does not use third-party analytics, advertising networks, or tracking tools.
        </p>

        {/* ================= SECTION 8 ================= */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">8. Changes to This Policy</h2>
        <p className="text-white/80 mb-4">
          We may update this Privacy Policy as BiteMark evolves or to meet legal requirements. The latest version will always be available on our website and within the app.
        </p>

        {/* ================= SECTION 9 ================= */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">9. Contact Us</h2>
        <p className="text-white/80 mb-4">
          If you have any questions about privacy or data handling, contact us at{" "}
          <a
            href="mailto:thebitemarkapp@gmail.com"
            className="underline hover:text-[#FFD369]"
          >
            thebitemarkapp@gmail.com
          </a>.
        </p>
      </main>

      {/* ================= FOOTER ================= */}
      <footer className="border-t border-white/10 py-8 mt-20">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-white/60">
          <div className="text-sm">
            © {new Date().getFullYear()} BiteMark — All rights reserved.
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm hover:text-white transition">
              Back to Home
            </Link>
            <Link to="/terms-of-use" className="text-sm hover:text-white transition">
              Terms of Use
            </Link>
            <Link to="/privacy-policy" className="text-sm hover:text-white transition">
              Privacy Policy
            </Link>
            <a
              href="mailto:thebitemarkapp@gmail.com"
              className="text-sm hover:text-white transition"
            >
              thebitemarkapp@gmail.com
            </a>
          </div>
        </div>
      </footer>

      <style>{`html { scroll-behavior: smooth; }`}</style>
    </div>
  );
}
